import { useCallback } from 'react'
import { isNull, isEmpty } from 'lodash-es'
import { useDispatch, useSelector } from 'react-redux'

import { constructRawVideoURL } from '../../sharedLegacy/utils'
import { actionNames } from '../shared/actions.playback'
import { dequeueVideo, getQueuedVideos, queueVideo } from '../shared/api'
import { showNotice } from '../../Layout/Notices/Notices'
import { useLiveId } from '../../global/blocs/roomInfo'

import usePlaybackIntent from './usePlaybackIntent'

const hasFetchedStagedVideos = (state) => state.webinarPlayback.videosFetched
const getPlaybackState = (state) => state.webinarPlayback.playbackState
const getSelectedPlaybackPhotoId = (state) =>
  state.webinarPlayback.selectedPlaybackPhotoId

const usePlaybackStagedVideos = () => {
  const liveId = useLiveId()
  const { setPlaybackStopIntent } = usePlaybackIntent()
  const dispatch = useDispatch()
  const selectedPlaybackPhotoId = useSelector(getSelectedPlaybackPhotoId)
  const playbackState = useSelector(getPlaybackState)
  const hasFetchedAlready = useSelector(hasFetchedStagedVideos)

  const unstagePlaybackVideo = useCallback(
    (video) => {
      // update locale state to remove it from the list immediately
      dispatch({ type: actionNames.UNSTAGE_PLAYBACK_VIDEO, video })
      const videoId = video.photo_id || video.live_id

      // stop the video if it is playing at the moment
      if (!isNull(playbackState) && selectedPlaybackPhotoId === videoId) {
        setPlaybackStopIntent()
      }

      try {
        // delete the vide from the list of staged videos on the server
        dequeueVideo(videoId, liveId)
      } catch (error) {
        // unhandled
      }
    },
    [
      liveId,
      dispatch,
      selectedPlaybackPhotoId,
      playbackState,
      setPlaybackStopIntent,
    ]
  )

  const loadPlaybackVideos = useCallback(async () => {
    dispatch({ type: actionNames.LOAD_PLAYBACK_VIDEOS, liveId })

    if (hasFetchedAlready) {
      return
    }

    try {
      const { photos } = await getQueuedVideos(liveId)
      const videos = photos.map((video) => {
        if (!isEmpty(video.protection_method)) {
          // TODO (noahlaux): Refactor to use weakmap for reference
          // eslint-disable-next-line no-param-reassign
          video.video_medium_download = constructRawVideoURL(
            video,
            video.protected_token
          )
        }

        return video
      })

      dispatch({ type: actionNames.STAGE_PLAYBACK_VIDEOS_SUCCESS, videos })
    } catch (error) {
      // unhandled
    }
  }, [liveId, dispatch, hasFetchedAlready])

  const stagePlaybackVideos = useCallback(
    async (videoList, added, removed) => {
      dispatch({
        type: actionNames.STAGE_PLAYBACK_VIDEOS,
        videos: videoList,
        added,
        removed,
      })

      const videos = []
      const addedVideos = new Set(added)

      videoList.forEach((video) => {
        if (!isEmpty(video.protection_method)) {
          // TODO (noahlaux): Refactor to use weakmap for reference
          // eslint-disable-next-line no-param-reassign
          video.video_medium_download = constructRawVideoURL(
            video,
            video.protected_token
          )
          videos.push(video)
        } else if (
          isEmpty(video.video_medium_download) &&
          isEmpty(video.video_hd_download)
        ) {
          showNotice({
            message: `Video ${video.title} could not be staged as it has no version in HD resolution`,
            expirationMillis: 5e3,
            isClosable: true,
          })
          addedVideos.delete(video.photo_id || video.live_id)
        } else {
          videos.push(video)
        }
      })

      try {
        addedVideos.forEach((videoId) => queueVideo(videoId, liveId))
        removed.forEach((videoId) => dequeueVideo(videoId, liveId))
      } catch (error) {
        // unhandled
      }

      dispatch({ type: actionNames.STAGE_PLAYBACK_VIDEOS_SUCCESS, videos })
    },
    [liveId, dispatch]
  )

  return { unstagePlaybackVideo, loadPlaybackVideos, stagePlaybackVideos }
}

export default usePlaybackStagedVideos
